
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import api from "../../utils/api";

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const HOADocumentsPage = () => {
  const [tab, setTab] = useState(0);

  // 🔹 Document state
  const [documents, setDocuments] = useState([]);
  const [uploads, setUploads] = useState([]); // [{file, description, category_id}]
  const [editDocId, setEditDocId] = useState(null);
  const [newName, setNewName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCategoryId, setEditCategoryId] = useState("");
  const dropRef = useRef(null);

  // 🔹 Category state
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryDescription, setNewCategoryDescription] = useState("");
  const [editCatId, setEditCatId] = useState(null);
  const [editCatName, setEditCatName] = useState("");
  const [editCatDescription, setEditCatDescription] = useState("");

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
  }, []);

  // ============ Document Functions ============
  const fetchDocuments = async () => {
    try {
      const res = await api.get(`/owner-documents`);
      setDocuments(res.data);
    } catch (err) {
      console.error("Failed to load documents", err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get(`/owner-document-categories`);
      setCategories(res.data);
    } catch (err) {
      console.error("Failed to load categories", err);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter((file) => file.size <= MAX_FILE_SIZE);

    if (validFiles.length < files.length) {
      alert("Some files exceeded the 100MB limit and were ignored.");
    }

    const withMeta = validFiles.map((file) => ({
      file,
      description: "",
      category_id: "",
    }));

    setUploads((prev) => [...prev, ...withMeta]);
  };

  const handleUpload = async () => {
    if (!uploads.length) return;

    const formData = new FormData();
    uploads.forEach((entry) => {
      formData.append("upload_urls", entry.file);
      formData.append("description", entry.description);
      formData.append("category_id", entry.category_id || "");
    });

    try {
      await api.post(`/owner-documents`, formData);
      setUploads([]);
      fetchDocuments();
    } catch (err) {
      console.error("Upload failed", err);
    }
  };

  const handleDeleteDoc = async (id) => {
    try {
      await api.delete(`/owner-documents/${id}`);
      fetchDocuments();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const handleEditDoc = (doc) => {
    setEditDocId(doc.id);
    setNewName(doc.original_filename);
    setEditDescription(doc.description || "");
    setEditCategoryId(doc.category_id || "");
  };

  const handleRenameDoc = async (id) => {
    try {
      await api.put(`/owner-documents/${id}`, {
        original_filename: newName,
        description: editDescription,
        category_id: editCategoryId,
      });
      setEditDocId(null);
      setNewName("");
      setEditDescription("");
      setEditCategoryId("");
      fetchDocuments();
    } catch (err) {
      console.error("Rename failed", err);
    }
  };

  const formatDate = (value) => {
    if (!value) return "Unknown";
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date) ? "Unknown" : date.toLocaleDateString();
  };

  // ============ Category Functions ============
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      await api.post(`/owner-document-categories`, {
        name: newCategoryName,
        description: newCategoryDescription,
      });
      setNewCategoryName("");
      setNewCategoryDescription("");
      fetchCategories();
    } catch (err) {
      console.error("Add category failed", err);
    }
  };

  const handleEditCategory = (cat) => {
    setEditCatId(cat.id);
    setEditCatName(cat.name);
    setEditCatDescription(cat.description || "");
  };

  const handleSaveCategory = async (id) => {
    try {
      await api.put(`/owner-document-categories/${id}`, {
        name: editCatName,
        description: editCatDescription,
      });
      setEditCatId(null);
      setEditCatName("");
      setEditCatDescription("");
      fetchCategories();
    } catch (err) {
      console.error("Update category failed", err);
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await api.delete(`/owner-document-categories/${id}`);
      fetchCategories();
    } catch (err) {
      alert("Cannot delete a category with assigned documents.");
      console.error("Delete category failed", err);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#fff",
    },
  };

  const selectSx = {
    borderRadius: "12px",
    backgroundColor: "#fff",
  };

  // ============ Render ============
  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 1100,
        mx: "auto",
        mt: 1,
        p: { xs: 2, md: 3 },
        borderRadius: "18px",
        border: "1px solid rgba(47,99,173,0.08)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        backgroundColor: "rgba(255,255,255,0.96)",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: "#223142",
          mb: 0.5,
        }}
      >
        HOA Document Manager
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: "#6b778c",
          mb: 3,
        }}
      >
        Upload, organize, and manage documents for HOA members.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          borderBottom: "1px solid #eef2f7",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
          },
        }}
      >
        <Tab label="Documents" />
        <Tab label="Categories" />
      </Tabs>

      {/* =================== Documents Tab =================== */}
      {tab === 0 && (
        <>
          {/* Drag and Drop Upload */}
          <Paper
            ref={dropRef}
            elevation={0}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFileDrop}
            sx={{
              p: 4,
              mb: 3,
              border: "2px dashed #b6c2d1",
              borderRadius: "14px",
              textAlign: "center",
              backgroundColor: "#fafbfd",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: "#2f63ad",
                backgroundColor: "#f6f9ff",
              },
            }}
          >
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Drag and drop files here
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              or select files manually
            </Typography>

            <input
              type="file"
              multiple
              style={{ marginTop: 10 }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                const validFiles = files.filter(
                  (file) => file.size <= MAX_FILE_SIZE
                );

                if (validFiles.length < files.length) {
                  alert("Some files exceeded the 100MB limit and were ignored.");
                }

                const withMeta = validFiles.map((file) => ({
                  file,
                  description: "",
                  category_id: "",
                }));

                setUploads((prev) => [...prev, ...withMeta]);
              }}
            />
          </Paper>

          {/* Pending Upload List */}
          {uploads.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: "16px",
                border: "1px solid rgba(47,99,173,0.08)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 700, color: "#223142" }}
              >
                Files Ready for Upload
              </Typography>

              <List sx={{ p: 0 }}>
                {uploads.map((item, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      px: 0,
                      py: 1.5,
                      borderBottom:
                        index < uploads.length - 1
                          ? "1px solid #eef2f7"
                          : "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 2,
                        width: "100%",
                        alignItems: { md: "center" },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                          {item.file.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(item.file.size / (1024 * 1024)).toFixed(2)} MB
                        </Typography>
                      </Box>

                      <TextField
                        label="Description"
                        value={item.description}
                        onChange={(e) => {
                          const updated = [...uploads];
                          updated[index].description = e.target.value;
                          setUploads(updated);
                        }}
                        size="small"
                        sx={{ minWidth: 220, ...fieldSx }}
                      />

                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={item.category_id}
                          label="Category"
                          onChange={(e) => {
                            const updated = [...uploads];
                            updated[index].category_id = e.target.value;
                            setUploads(updated);
                          }}
                          sx={selectSx}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </ListItem>
                ))}
              </List>

              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={handleUpload}
                sx={{
                  mt: 2,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#2f63ad",
                  "&:hover": { backgroundColor: "#1f4f94" },
                }}
              >
                Upload All
              </Button>
            </Paper>
          )}

          {/* List of Existing Documents */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "1px solid rgba(47,99,173,0.08)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 700, color: "#223142" }}
            >
              Uploaded Documents
            </Typography>

            {documents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No documents uploaded yet.
              </Typography>
            ) : (
              <>
                {Object.entries(
                  documents.reduce((acc, doc) => {
                    const category = doc.category_name || "Uncategorized";
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(doc);
                    return acc;
                  }, {})
                ).map(([category, docs]) => (
                  <Accordion
                    key={category}
                    defaultExpanded
                    elevation={0}
                    sx={{
                      borderRadius: "12px !important",
                      mb: 1,
                      border: "1px solid rgba(0,0,0,0.05)",
                      boxShadow: "none",
                      overflow: "hidden",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", color: "#223142" }}
                      >
                        {category} ({docs.length})
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails>
                      <List sx={{ p: 0 }}>
                        {docs.map((doc) => (
                          <ListItem
                            key={doc.id}
                            divider
                            sx={{
                              alignItems: "flex-start",
                              px: 0,
                              py: 1.5,
                            }}
                          >
                            {editDocId === doc.id ? (
                              <Box
                                sx={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1.5,
                                  pr: 8,
                                }}
                              >
                                <TextField
                                  label="Filename"
                                  value={newName}
                                  onChange={(e) => setNewName(e.target.value)}
                                  size="small"
                                  fullWidth
                                  sx={fieldSx}
                                />
                                <TextField
                                  label="Description"
                                  value={editDescription}
                                  onChange={(e) =>
                                    setEditDescription(e.target.value)
                                  }
                                  size="small"
                                  fullWidth
                                  sx={fieldSx}
                                />
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Category</InputLabel>
                                  <Select
                                    value={editCategoryId}
                                    label="Category"
                                    onChange={(e) =>
                                      setEditCategoryId(e.target.value)
                                    }
                                    sx={selectSx}
                                  >
                                    <MenuItem value="">
                                      <em>None</em>
                                    </MenuItem>
                                    {categories.map((cat) => (
                                      <MenuItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            ) : (
                              <ListItemText
                                primary={
                                  <Typography
                                    sx={{ fontWeight: 600, color: "#223142" }}
                                  >
                                    {doc.original_filename}
                                  </Typography>
                                }
                                secondary={`Description: ${
                                  doc.description || "N/A"
                                } | Uploaded on ${formatDate(doc.upload_date)}`}
                                sx={{ pr: 8 }}
                              />
                            )}

                            <ListItemSecondaryAction>
                              {editDocId === doc.id ? (
                                <IconButton onClick={() => handleRenameDoc(doc.id)}>
                                  <SaveIcon />
                                </IconButton>
                              ) : (
                                <IconButton onClick={() => handleEditDoc(doc)}>
                                  <EditIcon />
                                </IconButton>
                              )}
                              <IconButton onClick={() => handleDeleteDoc(doc.id)}>
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>
            )}
          </Paper>
        </>
      )}

      {/* =================== Categories Tab =================== */}
      {tab === 1 && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "16px",
            border: "1px solid rgba(47,99,173,0.08)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 700, color: "#223142" }}
          >
            Manage Categories
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              label="Category Name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              size="small"
              sx={{ flex: 1, ...fieldSx }}
            />
            <TextField
              label="Description"
              value={newCategoryDescription}
              onChange={(e) => setNewCategoryDescription(e.target.value)}
              size="small"
              sx={{ flex: 1, ...fieldSx }}
            />
            <Button
              variant="contained"
              onClick={handleAddCategory}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                minWidth: 120,
                backgroundColor: "#2f63ad",
                "&:hover": { backgroundColor: "#1f4f94" },
              }}
            >
              Add
            </Button>
          </Box>

          {categories.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No categories created yet.
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {categories.map((cat) => (
                <ListItem
                  key={cat.id}
                  divider
                  sx={{
                    px: 0,
                    py: 1.5,
                    alignItems: "flex-start",
                  }}
                >
                  {editCatId === cat.id ? (
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        pr: 8,
                      }}
                    >
                      <TextField
                        label="Name"
                        value={editCatName}
                        onChange={(e) => setEditCatName(e.target.value)}
                        size="small"
                        sx={fieldSx}
                      />
                      <TextField
                        label="Description"
                        value={editCatDescription}
                        onChange={(e) => setEditCatDescription(e.target.value)}
                        size="small"
                        sx={fieldSx}
                      />
                    </Box>
                  ) : (
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600, color: "#223142" }}>
                          {cat.name}
                        </Typography>
                      }
                      secondary={cat.description || "No description"}
                      sx={{ pr: 8 }}
                    />
                  )}

                  <ListItemSecondaryAction>
                    {editCatId === cat.id ? (
                      <IconButton onClick={() => handleSaveCategory(cat.id)}>
                        <SaveIcon />
                      </IconButton>
                    ) : (
                      <IconButton onClick={() => handleEditCategory(cat)}>
                        <EditIcon />
                      </IconButton>
                    )}
                    <IconButton onClick={() => handleDeleteCategory(cat.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}
    </Paper>
  );
};

export default HOADocumentsPage;

